<template>
  <UCard
    :ui="{
      root: 'relative isolate w-full h-full bg-neutral-900/60 backdrop-blur-lg overflow-hidden',
    }"
  >
    <NuxtImg
      v-if="gift.content.feature_image?.filename"
      provider="storyblok"
      :modifiers="{ smart: true }"
      sizes="xs:100svw sm:100svw md:100svw lg:25svw xl:100svw xxl:80svw"
      :src="gift.content.feature_image.filename"
      :alt="gift.content.feature_image.alt"
      class="absolute inset-0 h-full w-full object-cover object-center opacity-20"
      loading="lazy"
      placeholder
    />
    <div
      class="absolute inset-0 z-[1] h-full w-full bg-gradient-to-b from-transparent via-neutral-950/75 to-neutral-950"
    />
    <div
      class="relative z-[2] grid grid-cols-1 items-center gap-4 md:grid-cols-3"
    >
      <div class="col-span-2 w-full max-w-prose">
        <div class="text-base font-extrabold text-yellow-400 uppercase">
          Voucher
        </div>
        <h4 class="text-3xl font-extrabold">
          {{ gift.name || 'Untitled Gift' }}
        </h4>
        <p class="mt-1 text-lg font-medium text-neutral-400">
          {{ gift.content.lead }}
        </p>
        <div
          v-if="
            !enableGiftRedemption &&
            gift.content.activities?.length > 0 &&
            gift.content.gift_type === 'experience'
          "
          class="mt-1 flex flex-wrap items-center text-sm font-extrabold text-white"
        >
          <span class="mr-2 flex shrink-0 items-center space-x-1">
            <span>Learn More</span>
            <UIcon name="i-lucide-arrow-right" class="size-5" />
          </span>
          <UButton
            v-for="(activity, index) in gift.content.activities"
            :key="index"
            variant="link"
            :to="`/activities/${activity.slug}`"
            class="-ml-2"
            >{{ activity.name }}</UButton
          >
        </div>
      </div>
      <div v-if="enableGiftRedemption" class="flex flex-col space-y-2">
        <p
          v-if="
            gift.content.activities?.length > 1 &&
            gift.content.gift_type !== 'credit'
          "
          class="text-center font-bold text-white uppercase"
        >
          Redeem Voucher For:
        </p>
        <UButton
          v-for="(activity, index) in gift.content.activities"
          v-if="gift.content.gift_type !== 'credit'"
          :key="index"
          :to="`/activities/${activity.slug}?tab=availability`"
          class="justify-center text-center"
        >
          <span v-if="gift.content.activities?.length > 1">
            {{ activity.name }}
          </span>
          <span v-else>Redeem Voucher</span>
        </UButton>
        <UButton
          v-if="
            gift.content.gift_type === 'credit' &&
            !gift.name?.includes('Climbing')
          "
          :to="`/locations?tab=activities`"
          class="justify-center text-center"
        >
          Redeem At Any Activity Centre
        </UButton>
        <UButton
          v-if="
            gift.content.gift_type === 'credit' &&
            gift.name?.includes('Climbing')
          "
          :to="`/locations?tab=climbing`"
          class="justify-center text-center"
        >
          Redeem At Any Climbing Centre
        </UButton>
      </div>
      <UCard
        v-if="!enableGiftRedemption"
        :ui="{
          root: 'relative isolate bg-neutral-950/60 backdrop-blur-lg ring-yellow-400/50 overflow-hidden shadow-lg',
        }"
      >
        <div
          class="absolute inset-0 h-full w-full animate-pulse opacity-20"
          style="animation-duration: 5s"
        >
          <BrandTopography />
        </div>
        <div
          class="absolute inset-0 z-[1] h-full w-full bg-gradient-to-b from-transparent via-neutral-950/50 to-neutral-950"
        />
        <UBadge
          class="absolute top-3 right-3 z-[3] rounded-full bg-neutral-800 p-1.5 backdrop-blur-lg"
        >
          <UIcon name="i-lucide-gift" class="size-5 text-yellow-400" />
        </UBadge>
        <div class="relative z-[2] space-y-2 text-center">
          <div>
            <div
              v-if="gift.content.price_starting_from"
              class="text-xs font-extrabold text-yellow-400 uppercase"
            >
              Starting At
            </div>
            <div class="text-5xl font-extrabold">
              £{{ formatPrice(gift.content.price) }}
            </div>
            <div class="-mt-0.5 pb-2 font-bold text-neutral-400">
              Per Voucher
            </div>
            <UButton
              v-if="gift.content.booking_type === 'link'"
              :to="gift.content.booking_link?.url"
              target="_blank"
              size="xl"
              class="w-full justify-center text-center"
              @click="handleBuyNowClick"
            >
              Buy Now
            </UButton>
          </div>
        </div>
      </UCard>
    </div>
  </UCard>
</template>

<script setup lang="ts">
import type { GiftStoryblok } from '~/types/component-types-sb';

const { proxy } = useScriptGoogleAnalytics();
const { formatPrice } = useFormatPrice();

defineProps<{
  gift: GiftStoryblok;
  type: string;
  enableGiftRedemption?: boolean;
}>();

const handleBuyNowClick = () => {
  // @ts-expect-error - gtag types are not fully typed
  proxy.gtag('event', 'voucher_buy_now_clicked');
  // @ts-expect-error - Posthog is available globally
  $clientPosthog?.capture('voucher_buy_now_clicked');
};
</script>
